import { useCallback, useEffect, useState } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import useRedux from 'redux/useRedux';

export type BadgeCounts = { [key: number]: number };

interface StatusCountersResponse {
  pending: number;
  newOrder: number;
  process: number;
  ready: number;
  delivery: number;
  complained: number;
  waitingForConfirmation: number;
  completed: number;
  canceled: number;
}

let globalBadgeCounts: BadgeCounts = {};
const listeners = new Set<(counts: BadgeCounts) => void>();

const setGlobalBadgeCounts = (next: BadgeCounts) => {
  globalBadgeCounts = next;
  listeners.forEach((listener) => listener(globalBadgeCounts));
};

export const useOrderStatusCounters = () => {
  const {
    storeState: { Auth },
  } = useRedux();

  const [badgeCounts, setBadgeCounts] = useState<BadgeCounts>(globalBadgeCounts);
  const [loadingCounters, setLoadingCounters] = useState(false);

  useEffect(() => {
    const listener = (next: BadgeCounts) => setBadgeCounts(next);
    listeners.add(listener);

    return () => {
      listeners.delete(listener);
    };
  }, []);

  const fetchStatusCounters = useCallback(async () => {
    try {
      const token = Auth?.data?.data?.token;
      const merchantId = Auth?.data?.data?.merchant?.id;
      if (!token || !merchantId) return;

      setLoadingCounters(true);

      const config: AxiosRequestConfig = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/orders/status-counters?merchant_id=${merchantId}&is_service=0`,
        config
      );

      const counters: StatusCountersResponse | undefined = res.data?.data;
      if (!counters) return;

      const mapped: BadgeCounts = {
        0: counters.pending,
        1: counters.newOrder,
        2: counters.process,
        3: counters.ready,
        4: counters.delivery,
        5: counters.complained,
        6: counters.waitingForConfirmation,
        7: counters.completed,
        8: counters.canceled,
      };

      setGlobalBadgeCounts(mapped);
    } catch (err) {
      console.error('Failed to fetch status counters', err);
    } finally {
      setLoadingCounters(false);
    }
  }, [Auth?.data?.data?.token, Auth?.data?.data?.merchant?.id]);

  useEffect(() => {
    fetchStatusCounters();
  }, [fetchStatusCounters]);

  return {
    badgeCounts,
    loadingCounters,
    refreshStatusCounters: fetchStatusCounters,
  };
};
